package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.animation.AnimationSetup
import dev.danko.roblox_craft_ai.shared.animation.WeaponKind
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Img

/** What a hand holds: a built-in weapon ([model] null) or one of the user's models moving like [kind]. */
data class WeaponChoice(val kind: WeaponKind, val model: LibraryModel? = null)

val WEAPON_ICONS = mapOf(WeaponKind.SWORD to AppIcon.SWORD, WeaponKind.GUN to AppIcon.GUN, WeaponKind.STAFF to AppIcon.STAFF, WeaponKind.AXE to AppIcon.AXE)

/**
 * The weapons a hand can hold, in one grid: the built-in ones (icons) and the user's models from «Мои работы»
 * (pictures and names). A model moves like the weapon its name suggests (a bat swings like an axe).
 */
@Composable
fun WeaponPicker(
    title: String,
    models: List<LibraryModel>,
    selectedKind: WeaponKind,
    selectedModelUrl: String?,
    allowStaff: Boolean = true,
    enabled: Boolean = true,
    onSelect: (WeaponChoice) -> Unit
) {
    val a = strings().animationSetup
    val palette = LocalAppTheme.current.colors
    val builtIn = listOf(WeaponKind.SWORD to a.sword, WeaponKind.GUN to a.gun, WeaponKind.STAFF to a.staff, WeaponKind.AXE to a.axe)
        .filter { allowStaff || it.first != WeaponKind.STAFF }
    Column(Modifier.fillMaxWidth().gap(4.px)) {
        SpanText(title, Modifier.fontSize(11.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary))
        Row(Modifier.fillMaxWidth().gap(4.px).flexWrap(FlexWrap.Wrap)) {
            builtIn.forEach { (kind, label) ->
                WeaponCard(label, null, WEAPON_ICONS.getValue(kind), "$label — ${a.weaponHint(kind.name)}",
                    selectedModelUrl == null && selectedKind == kind, enabled) { onSelect(WeaponChoice(kind)) }
            }
            models.take(10).forEach { model ->
                val kind = AnimationSetup.guessKind(model.title + " " + model.prompt).let { if (!allowStaff && it == WeaponKind.STAFF) WeaponKind.SWORD else it }
                val moves = builtIn.firstOrNull { it.first == kind }?.second ?: a.sword
                WeaponCard(model.title, model.image, AppIcon.CUBE, "${model.title} — ${a.weaponMovesLike(moves)}",
                    selectedModelUrl == model.glbUrl, enabled) { onSelect(WeaponChoice(kind, model)) }
            }
        }
    }
}

@Composable
private fun WeaponCard(label: String, image: String?, icon: AppIcon, tooltip: String, isSelected: Boolean, enabled: Boolean, onClick: () -> Unit) {
    val palette = LocalAppTheme.current.colors
    Button(
        Modifier.width(68.px).height(66.px).padding(3.px).borderRadius(10.px)
            .border(1.px, LineStyle.Solid, if (isSelected) palette.accent else palette.borderStrong)
            .backgroundColor(if (isSelected) palette.accent.toRgb().copy(alpha = 40) else Colors.Transparent)
            .color(if (isSelected) palette.textPrimary else palette.textSecondary)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .opacity(if (enabled) 1.0 else 0.6)
            .styleModifier { property("font-family", "inherit") }
            .onClick { if (enabled) onClick() }
            .toAttrs {
                attr("type", "button")
                attr("role", "radio")
                attr("aria-checked", isSelected.toString())
                attr("aria-label", tooltip)
                attr("title", tooltip.take(300))
                if (!enabled) attr("disabled", "")
            }
    ) {
        Column(Modifier.fillMaxWidth().gap(2.px), horizontalAlignment = Alignment.CenterHorizontally) {
            Box(Modifier.width(56.px).height(34.px).borderRadius(6.px).styleModifier { property("overflow", "hidden") }, contentAlignment = Alignment.Center) {
                if (image != null) {
                    Img(src = BackendEndpoint.resolveAssetUrl(image), attrs = Modifier.fillMaxWidth().fillMaxHeight().objectFit(ObjectFit.Cover).toAttrs { attr("alt", ""); attr("loading", "lazy") })
                } else {
                    Box(Modifier.color(if (isSelected) palette.accent else palette.textSecondary)) { Icon(icon, size = 20, strokeWidth = 1.8) }
                }
            }
            SpanText(
                label,
                Modifier.fontSize(10.px).fontWeight(FontWeight.SemiBold).fillMaxWidth().lineHeight(1.1)
                    .styleModifier {
                        property("text-align", "center"); property("overflow", "hidden")
                        property("display", "-webkit-box"); property("-webkit-line-clamp", "2"); property("-webkit-box-orient", "vertical")
                    }
            )
        }
    }
}
